/**
 * Idle expiry — the bound the owner put on shared storage.
 *
 * AR-2c moves the session into storage every tab shares, so Dana signs in once
 * per browser instead of once per tab. The cost is that the session no longer
 * dies with the tab: on a shared office machine it would outlive her until an
 * explicit logout or refresh-token expiry. The owner accepted shared storage
 * only with this bound — after VITE_AUTH_IDLE_HOURS (default 8) with no one at
 * the keyboard, the session ends, in every tab at once.
 *
 * Activity is user INPUT — pointer, key, wheel, touch — and nothing else.
 * Authenticated API calls do not count, though the charter named them: the
 * notification bell polls /api/notifications/unread-count every 30 seconds
 * from every page (hooks/useNotifications.ts), so a tab left open would renew
 * its own session forever and the bound would never fire in the one situation
 * it exists for. Every API call a person causes is preceded by input anyway.
 *
 * The clock lives under its own key, beside the session rather than inside
 * it. Inside it, every write of the session — a sibling adopting a rotation, a
 * boot finishing hydration — would carry that tab's own idea of the last
 * activity, which can be hours stale, and wind the shared clock backwards
 * until a tab expired a user who was working in another one. A key that is
 * only ever written with the current time can only move forward.
 *
 * Expiry ends the session through the router, not a document load: the store
 * signs out, the route guard sees an unauthenticated render and captures where
 * she was, and signing back in resumes there. Siblings are told with a
 * signed-out envelope whose signOutReason is 'idle', not by removing the key,
 * because a removal is how an explicit logout is announced — and that one
 * deliberately captures nothing (lib/authSync.ts).
 */
import { useAuthStore, authStorageArea } from '@/stores/authStore'

/** Where the shared last-activity clock lives, beside the session. */
export const ACTIVITY_STORAGE_KEY = 'clearpoint-auth-activity'

const HOUR_MS = 3_600_000
const DEFAULT_IDLE_HOURS = 8

/** At most one clock write per minute — across every tab, not per tab. */
export const ACTIVITY_WRITE_INTERVAL_MS = 60_000

/** How often an open tab reads the clock when nothing else prompts it. */
export const IDLE_CHECK_INTERVAL_MS = 60_000

/** The input that means someone is at the keyboard. */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const

/** This tab's own latest activity — what still counts if storage cannot be read. */
let localActivityAt = 0
/** When this tab last wrote the shared clock. */
let lastWriteAt = 0

/**
 * The idle bound in milliseconds, from VITE_AUTH_IDLE_HOURS.
 *
 * Baked in at build time like the other VITE_* values; the Dockerfile passes
 * it through. Anything that is not a positive number falls back to the
 * default rather than to no bound at all — a typo in a deployment variable
 * must not quietly switch the owner's bound off. The empty string is the
 * common case, not an edge: an unset ARG still reaches the build as ''.
 */
export function resolveIdleBoundMs(
  env: { VITE_AUTH_IDLE_HOURS?: string } = import.meta.env,
): number {
  const hours = Number.parseFloat(env.VITE_AUTH_IDLE_HOURS ?? '')
  return (Number.isFinite(hours) && hours > 0 ? hours : DEFAULT_IDLE_HOURS) * HOUR_MS
}

function readStoredActivity(): number {
  try {
    const raw = authStorageArea().getItem(ACTIVITY_STORAGE_KEY)
    const at = raw === null ? 0 : Number(raw)
    return Number.isFinite(at) ? at : 0
  } catch {
    return 0
  }
}

function writeActivity(now: number): void {
  lastWriteAt = now
  try {
    authStorageArea().setItem(ACTIVITY_STORAGE_KEY, String(now))
  } catch {
    // Blocked or full storage. localActivityAt still carries this tab.
  }
}

/**
 * The last activity anywhere in this browser: the shared clock, or this tab's
 * own when storage is unreadable.
 *
 * No clock at all beside a live session means the clock was lost, not that
 * someone was just here, so it reads as the epoch and the session fails
 * closed. Every sign-in writes the clock before the session, so the only
 * sessions this catches are ones that should not be trusted anyway.
 */
export function lastActivityAt(): number {
  return Math.max(readStoredActivity(), localActivityAt)
}

/**
 * End the session if it has been idle past the bound. Returns whether it did.
 *
 * Only a tab holding a session has one to end. expireForIdle() signs the store
 * out and writes the idle envelope; the route guard and the siblings do the
 * rest.
 */
export function expireIfIdle(
  now: number = Date.now(),
  boundMs: number = resolveIdleBoundMs(),
): boolean {
  const { accessToken, user, expireForIdle } = useAuthStore.getState()
  if (!accessToken && !user) return false
  if (now - lastActivityAt() <= boundMs) return false
  expireForIdle()
  return true
}

/**
 * Someone is at the keyboard.
 *
 * The clock is read BEFORE it is wound: the first thing she does on returning
 * to a stale tab is click, and if that click counted first it would carry the
 * clock forward and quietly rescue a session the bound had already ended.
 *
 * Throttled to one write a minute across every tab — skipped whenever the
 * shared clock, whoever set it, is already that fresh — so N open tabs cost
 * the same as one.
 */
export function recordActivity(
  now: number = Date.now(),
  boundMs: number = resolveIdleBoundMs(),
): void {
  if (expireIfIdle(now, boundMs)) return
  localActivityAt = Math.max(localActivityAt, now)
  // No session, nothing to keep alive: typing a password on /login is not
  // activity on a session that does not exist yet. login() starts its own.
  if (!useAuthStore.getState().accessToken) return
  if (now - Math.max(readStoredActivity(), lastWriteAt) < ACTIVITY_WRITE_INTERVAL_MS) return
  writeActivity(now)
}

/**
 * A session is starting: set the clock unconditionally.
 *
 * Called by login() BEFORE the session is written, so no tab can see the new
 * session beside the previous one's stale clock and expire it on arrival.
 */
export function startActivityClock(now: number = Date.now()): void {
  localActivityAt = now
  writeActivity(now)
}

/**
 * Forget this tab's clock. Tests only: module state outlives a test case, and
 * a clock left over from one would answer for the next.
 */
export function resetActivityClockForTests(): void {
  localActivityAt = 0
  lastWriteAt = 0
}

/**
 * Start watching for activity and idleness. Returns a disposer.
 *
 * Reads the clock once immediately — the boot check: a tab opened on Monday
 * onto Friday's session expires before its first render, so the guard captures
 * the link she clicked and login finishes the trip. Then on focus and whenever
 * the tab becomes visible, because browsers throttle a background tab's timers
 * and the first thing she does on returning is look at it; and on an interval,
 * for the tab that is never looked at again.
 */
export function startIdleWatch(): () => void {
  const onActivity = () => recordActivity()
  const check = () => {
    expireIfIdle()
  }
  const onVisibility = () => {
    if (document.visibilityState === 'visible') check()
  }

  check()
  for (const type of ACTIVITY_EVENTS) {
    window.addEventListener(type, onActivity, { capture: true, passive: true })
  }
  window.addEventListener('focus', check)
  document.addEventListener('visibilitychange', onVisibility)
  const interval = window.setInterval(check, IDLE_CHECK_INTERVAL_MS)

  return () => {
    for (const type of ACTIVITY_EVENTS) {
      window.removeEventListener(type, onActivity, { capture: true })
    }
    window.removeEventListener('focus', check)
    document.removeEventListener('visibilitychange', onVisibility)
    window.clearInterval(interval)
  }
}
